import React, { useEffect, useState, useMemo } from 'react';
import toast from 'react-hot-toast';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { Spinner } from 'reactstrap';
import { Controller, useForm } from 'react-hook-form';
import { setInterviewInvites } from 'slices/interviewInviteSlice';
import { acceptInterviewInvites, rejectInterviewInvites, fetchInterviewInvites, interviewInvitesSelector } from 'slices/interviewInviteSlice';
import Swal from 'sweetalert2';
import InterviewInviteTable from './InterviewInviteTable';
import { Select } from 'components/common/Select';
import ErrorMessage from 'components/common/ErrorMessage';
import { useTranslation } from 'react-i18next';

const InterviewInvite = () => {
    const { t } = useTranslation();
    const { InterviewInvite, loader } = useSelector(interviewInvitesSelector);
    const [statusMessage, setStatusMessage] = useState();
    const [status, setStatus] = useState();
    const dispatch = useDispatch();
    const params = new URLSearchParams(window.location.search);
    const [token, setToken] = useState(params?.get("token"));

    const interpreter = InterviewInvite?.invite_details?.interpreter || InterviewInvite?.interpreter;
    const isAgencyVendor = interpreter?.vendor_type === 'agency';
    const agencyInterpreterList = useMemo(() => {
        return interpreter?.agency_interpreters || interpreter?.agencyInterpreters || [];
    }, [interpreter]);

    const agencyOptions = useMemo(() => {
        const list = agencyInterpreterList.map(item => ({
            value: item.id,
            // label: (item.code ? `${item.code} - ` : '') + item.name,
            label: item.name,
        }));
        list.push({
            value: 'manual',
            label: '+ Create New Agency Interpreter',
        });
        return list;
    }, [agencyInterpreterList]);

    const methods = useForm({
        defaultValues: {
            agency_interpreter_selection: null,
            agency_interpreter_id: null,
            agency_interpreter_name: '',
            agency_interpreter_code: '',
        }
    });

    const { handleSubmit, formState: { errors }, control, setValue, watch } = methods;

    const selectedAgencySelection = watch('agency_interpreter_selection');

    useEffect(() => {
        dispatch(fetchInterviewInvites(token, submitCallback));
        return () => {
            dispatch(setInterviewInvites({}));
        };
    }, [token]);

    useEffect(() => {
        const inviteDetails = InterviewInvite?.invite_details;
        if (inviteDetails) {
            if (inviteDetails.agency_interpreter_id) {
                setValue('agency_interpreter_selection', inviteDetails.agency_interpreter_id);
                setValue('agency_interpreter_id', inviteDetails.agency_interpreter_id);
                setValue('agency_interpreter_name', inviteDetails.agency_interpreter_name || '');
                setValue('agency_interpreter_code', inviteDetails.agency_interpreter_code || '');
            } else if (inviteDetails.agency_interpreter_name) {
                setValue('agency_interpreter_selection', 'manual');
                setValue('agency_interpreter_id', null);
                setValue('agency_interpreter_name', inviteDetails.agency_interpreter_name || '');
                setValue('agency_interpreter_code', inviteDetails.agency_interpreter_code || '');
            }
        }
    }, [InterviewInvite, setValue]);

    const submitCallback = (status, message) => {
        if (status) {
            toast.success(message, {
                className: "successToast",
            });
            setStatusMessage(message);
        } else {
            toast.error(message, {
                className: "errorToast",
            });
            setStatusMessage(message);
            setStatus(status);
        }
    };

    const handleAcceptSubmit = (formData) => {
        let payload = {};
        if (isAgencyVendor) {
            if (selectedAgencySelection === 'manual') {
                payload = {
                    agency_interpreter_code: formData.agency_interpreter_code ? formData.agency_interpreter_code.trim() : '',
                    agency_interpreter_name: formData.agency_interpreter_name ? formData.agency_interpreter_name.trim() : '',
                };
            } else if (formData.agency_interpreter_id) {
                payload = {
                    agency_interpreter_id: formData.agency_interpreter_id,
                };
            }
        }

        dispatch(acceptInterviewInvites(token, payload, (success, message) => {
            submitCallback(success, message);
            if (success) {
                const isAutoAssign = InterviewInvite?.appointment_details?.auto_assign === 1 && InterviewInvite?.invite_details?.interpreter?.auto_assign === 1;
                Swal.fire({
                    title: isAutoAssign ? "Appointment Assigned!" : "Thanks for Responding!",
                    text: isAutoAssign
                        ? "Thanks for accepting the appointment. It has been assigned to you and you can add it to your calendar"
                        : "Thanks for letting us know. Please note you're not yet assigned. Standby to hear back from the team on this assignment",
                    icon: "success",
                });
                dispatch(fetchInterviewInvites(token, submitCallback));
            }
        }));
        setStatus(status);
    };

    function RejectInvite() {
        dispatch(rejectInterviewInvites(token, submitCallback));
        Swal.fire({
            title: "Appointment Rejected!",
            icon: "error",
        });
        setTimeout(() => {
            window.location.reload();
        }, 2000);
    }

    if (loader) {
        return (
            <div className="loader_wrapper">
                <Spinner size="xl">
                    Loading...
                </Spinner>
            </div>
        );
    }

    const inviteStatus = InterviewInvite?.invite_details?.status;
    const assignedInterpreterId = InterviewInvite?.interpreter_id;
    const currentInterpreterId = InterviewInvite?.invite_details?.interpreter?.id;

    return (
        <>
            {statusMessage === 'Data retrieved successfully.' && InterviewInvite ? (
                <div className='main-freePage'>
                    <img src="/images/logo.png" alt="" />
                    <div className='table_wrapper' style={{ overflow: 'unset' }}>
                        <div className="appointment-container">
                            <div className="appointment-header">
                                <p>Appt <b className='font-semibold'># {InterviewInvite?.appid}</b></p>
                            </div>
                            <InterviewInviteTable InterviewInvite={InterviewInvite} />

                            {InterviewInvite?.status === 'available' && inviteStatus !== 'accepted' && inviteStatus !== 'expired' && inviteStatus !== 'cancelled' ? (
                                <form onSubmit={handleSubmit(handleAcceptSubmit)} style={{ marginTop: '20px' }}>
                                    {isAgencyVendor && (
                                        <div className="agency-interpreter-section w-100" style={{ marginBottom: 20 }}>
                                            <div className="row-primary" style={{ marginBottom: selectedAgencySelection === 'manual' ? 15 : 0 }}>
                                                <h2>{t("selectInterpreter.agencyInterpreter", "Agency Interpreter")} *</h2>
                                                <Controller
                                                    name="agency_interpreter_selection"
                                                    control={control}
                                                    rules={{ required: t("selectInterpreter.agencyInterpreterRequired", "Please select or create an Agency Interpreter") }}
                                                    render={({ field }) => (
                                                        <div style={{ width: '100%' }}>
                                                            <Select
                                                                name="agency_interpreter_selection"
                                                                options={agencyOptions}
                                                                value={field.value
                                                                    ? agencyOptions.find(opt => opt.value == field.value) ?? null
                                                                    : null
                                                                }
                                                                onChange={(rawValue) => {
                                                                    field.onChange(rawValue ?? null);
                                                                    if (rawValue && rawValue !== 'manual') {
                                                                        const selectedObj = agencyInterpreterList.find(a => a.id == rawValue);
                                                                        setValue('agency_interpreter_id', rawValue);
                                                                        setValue('agency_interpreter_name', selectedObj?.name || '');
                                                                        setValue('agency_interpreter_code', selectedObj?.code || '');
                                                                    } else {
                                                                        setValue('agency_interpreter_id', null);
                                                                        setValue('agency_interpreter_name', '');
                                                                        setValue('agency_interpreter_code', '');
                                                                    }
                                                                }}
                                                            />
                                                            {errors?.agency_interpreter_selection?.message && (
                                                                <div style={{ marginTop: 5, marginBottom: 10 }}>
                                                                    <ErrorMessage text={errors.agency_interpreter_selection.message} />
                                                                </div>
                                                            )}
                                                        </div>
                                                    )}
                                                />
                                            </div>

                                            {selectedAgencySelection === 'manual' && (
                                                <div className="manual-agency-interpreter-fields" style={{ marginTop: 15 }}>
                                                    <div className="row-primary" style={{ marginBottom: 15 }}>
                                                        <h2>{t("selectInterpreter.agencyInterpreterCode", "Agency Interpreter ID")}</h2>
                                                        <Controller
                                                            name="agency_interpreter_code"
                                                            control={control}
                                                            render={({ field }) => (
                                                                <input
                                                                    {...field}
                                                                    className="input-in type_inp"
                                                                    placeholder={t("selectInterpreter.enterAgencyInterpreterCode", "Enter Agency Interpreter ID")}
                                                                />
                                                            )}
                                                        />
                                                    </div>

                                                    <div className="row-primary">
                                                        <h2>{t("selectInterpreter.agencyInterpreterName", "Agency Interpreter Name")} *</h2>
                                                        <Controller
                                                            name="agency_interpreter_name"
                                                            control={control}
                                                            rules={{ required: t("selectInterpreter.nameRequired", "Agency Interpreter Name is required") }}
                                                            render={({ field }) => (
                                                                <input
                                                                    {...field}
                                                                    className="input-in type_inp"
                                                                    placeholder={t("selectInterpreter.enterAgencyInterpreterName", "Enter Agency Interpreter Name")}
                                                                />
                                                            )}
                                                        />
                                                        {errors?.agency_interpreter_name?.message && (
                                                            <ErrorMessage text={errors.agency_interpreter_name.message} />
                                                        )}
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    )}

                                    <div className='str_row'>
                                        <div className="agreement-section">
                                        </div>
                                        <div className="button-group">
                                            <button type="submit" className="main_button seagreen">
                                                I'm Available
                                            </button>
                                            <button type="button" className="table_delete" onClick={RejectInvite}>
                                                Decline
                                            </button>
                                        </div>
                                    </div>
                                </form>
                            ) : inviteStatus === 'accepted' && assignedInterpreterId === currentInterpreterId  ? (
                                <div className='str_row'>
                                    <p style={{ color: 'green', fontWeight: 500 }}>
                                        Appointment invite accepted successfully.
                                    </p>
                                </div>
                            ) : inviteStatus === 'accepted' && InterviewInvite?.status !== 'assigned' ? (
                                <div className='str_row'>
                                    <p style={{ color: 'green', fontWeight: 500 }}>
                                        Appointment invite accepted successfully.
                                    </p>
                                </div>
                            // ) : InterviewInvite?.status === 'assigned' ? (
                            ) : InterviewInvite?.status === 'assigned' || inviteStatus === 'expired' ? (
                                <div className='str_row'>
                                    <p style={{ color: 'red', fontWeight: 500 }}>
                                        This appointment has already been assigned to someone else.
                                    </p>
                                </div>
                            ) : inviteStatus === 'cancelled' ? (
                                <div className='str_row'>
                                    <p style={{ color: 'red', fontWeight: 500 }}>
                                        This job is no longer available.
                                    </p>
                                </div>
                            ) : InterviewInvite?.status === 'cancelled' ? (
                                <div className='str_row'>
                                    <p style={{ color: 'red', fontWeight: 500 }}>
                                        This appointment has been cancelled.
                                    </p>
                                </div>
                            ) : null}

                        </div>
                    </div>
                </div>
            ) : statusMessage === 'Page Expired' ? (
                <div className="error-page-container">
                    <img src="/images/logo.png" alt="" />
                    <h4>OOps! {statusMessage}</h4>
                    <Link to="/" className="main_button mt-5">
                        Go to home page
                    </Link>
                </div>
            ) : (
                <div className="error-page-container">
                    <img src="/images/logo.png" alt="" />
                    <h4>Oopps! The page you were looking for doesn't exist.</h4>
                    <p>
                        <Link to="/" className="button">
                            Go to home page
                        </Link>
                    </p>
                </div>
            )}
        </>
    );
};

export default InterviewInvite;
